import React, { useState, useCallback, useEffect } from "react";
import TrainingPartList from "./TrainingPartList";
import { useNavigate } from "react-router-dom";
import trainingPeriodService from "../../services/trainingPeriodService";

const TrainingForm = ({ training, onSave }) => {
  const [trainingData, setTrainingData] = useState(training);
  const [availablePeriods, setAvailablePeriods] = useState([]);
  const navigate = useNavigate();

  // Period loading function
  const loadTrainingPeriods = useCallback(async () => {
    try {
      const startDate = new Date(trainingData.trainingDate);
      startDate.setHours(0, 0, 0, 0);
      const endDate = new Date(trainingData.trainingDate);
      endDate.setHours(23, 59, 59, 999);

      let periods = await trainingPeriodService.getByDateForRange(startDate, endDate);
      periods = periods.sort((a, b) => new Date(a.startDate) - new Date(b.startDate));

      // Add default "Brak" and set initial period if not set
      const periodsWithDefault = [{ id: null, name: "Brak" }, ...periods];
      setAvailablePeriods(periodsWithDefault);

      if (!trainingData.trainingPeriodId && periods.length > 0) {
        setTrainingData((prev) => ({
          ...prev,
          trainingPeriodId: periods[0].id,
        }));
      }
    } catch (error) {
      console.error("Error loading training periods:", error);
    }
  },  [trainingData.trainingDate, trainingData.trainingPeriodId]);

  useEffect(() => {
    loadTrainingPeriods(); // Load periods when component mounts or training date changes
  }, [loadTrainingPeriods]);

  // **Handle Save** - Delegates saving to the parent component via `onSave` prop
  const handleSave = (event) => {
    event.preventDefault();
    console.log("Saving data:", trainingData);

    // Use the parent-provided onSave function
    onSave(trainingData);
    navigate("/trainings"); // Navigate back to training list
  };

  const handleCancel = (event) => {
    event.preventDefault();
    navigate("/trainings");
  };

  const handleChange = (event) => {
    const { id, value } = event.target;
    setTrainingData((prev) => ({ ...prev, [id]: value }));
  };

  const handleTrainingDateTimeChange = (event) => {
    setTrainingData((prev) => ({
      ...prev,
      trainingDate: new Date(event.target.value),
    }));
  };

  const handlePeriodChange = (event) => {
    const periodId = event.target.value === "null" ? null : parseInt(event.target.value, 10);
    setTrainingData((prev) => ({
      ...prev,
      trainingPeriodId: periodId,
    }));
  };

  const handleUpdateTrainingPartList = (newPartList) => {
    setTrainingData((prev) => ({
      ...prev,
      trainingParts: newPartList,
    }));
  };

  // Format date for display in local timezone
  const trainingDateGMT = new Date(trainingData.trainingDate);
  const localTrainingDate = new Date(
    trainingDateGMT.getTime() - 1000 * 60 * trainingDateGMT.getTimezoneOffset()
  );

  return (
    <div className="container">
      <form>
        <div className="row">
          {/* Training Date */}
          <div className="form-group col-md-2">
            <label htmlFor="trainingDate">Data treningu</label>
            <input
              id="trainingDate"
              type="datetime-local"
              className="form-control"
              value={localTrainingDate.toISOString().substring(0, 19)}
              onChange={handleTrainingDateTimeChange}
            />
          </div>

          {/* Training Name */}
          <div className="form-group col-md-6">
            <label htmlFor="name">Nazwa treningu</label>
            <input
              id="name"
              type="text"
              className="form-control"
              value={trainingData.name}
              onChange={handleChange}
            />
          </div>

          {/* Action Buttons */}
          <div className="form-group col-md-3 d-flex align-items-end float-right">
            <button className="btn btn-primary px-4" onClick={handleSave}>
              Zapisz
            </button>
            <button className="btn btn-warning px-4 mx-2" onClick={handleCancel}>
              Anuluj
            </button>
          </div>
        </div>

        <div className="row">
          {/* Training Type */}
          <div className="form-group col-md-2">
            <label htmlFor="trainingType">Typ treningu</label>
            <select
              id="trainingType"
              className="form-select"
              value={trainingData.trainingType}
              onChange={handleChange}
            >
              <option value="RUNNING">Biegowy</option>
              <option value="ALTERNATIVE">Alternatywny</option>
              <option value="SUPPLEMENTARY">Uzupełniający</option>
              <option value="SUPPLEMENTARY_TECHNICAL">Uzupełnienie techniczne</option>
            </select>
          </div>

          {/* Distance */}
          <div className="form-group col-md-1">
            <label htmlFor="distance">Dystans</label>
            <input
              id="distance"
              type="number"
              className="form-control"
              value={trainingData.distance}
              onChange={handleChange}
            />
          </div>

          {/* Elevation Gain */}
          <div className="form-group col-md-1">
            <label htmlFor="elevationGain">Wznios</label>
            <input
              id="elevationGain"
              type="number"
              className="form-control"
              value={trainingData.elevationGain}
              onChange={handleChange}
            />
          </div>

          {/* Training Period */}
          <div className="form-group col-md-2">
            <label htmlFor="trainingPeriodId">Okres treningowy</label>
            <select
              id="trainingPeriodId"
              className="form-select"
              value={trainingData.trainingPeriodId || "null"}
              onChange={handlePeriodChange}
            >
              {availablePeriods.map((period) => (
                <option key={period.id} value={period.id}>
                  {period.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Training Description */}
        <div className="form-group">
          <label htmlFor="description">Opis treningu</label>
          <textarea
            className="form-control"
            id="description"
            rows="5"
            value={trainingData.description}
            onChange={handleChange}
          />
        </div>
      </form>

      <TrainingPartList
        training={trainingData}
        updateTrainingPartList={handleUpdateTrainingPartList}
      />
    </div>
  );
};

export default TrainingForm;
